/**
 * Step 3: 视觉风格
 * - 插画风格（2x2网格大卡片）- 从数据库动态拉取
 * - 高级设置（可折叠面板：语言模型、音频音色）
 */
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCreationStore } from '../../../store/useCreationStore';
import { ChevronDown, Wand2, Volume2, Sparkles, Loader2, Gauge } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// 根据 styleCategory 映射 UI 样式
const categoryStyleMap: Record<string, { emoji: string; gradient: string; previewColor: string }> = {
  '3d':       { emoji: '🎬', gradient: 'from-blue-100 via-indigo-50 to-purple-100', previewColor: 'bg-blue-200' },
  'watercolor': { emoji: '🎨', gradient: 'from-rose-100 via-amber-50 to-orange-100', previewColor: 'bg-rose-200' },
  'sketch':   { emoji: '✏️', gradient: 'from-gray-100 via-slate-50 to-zinc-100', previewColor: 'bg-gray-200' },
  'flat':     { emoji: '🖼️', gradient: 'from-emerald-100 via-teal-50 to-cyan-100', previewColor: 'bg-emerald-200' },
};

function getCategoryStyle(category?: string | null) {
  return categoryStyleMap[category || ''] || { emoji: '✨', gradient: 'from-slate-100 via-gray-50 to-zinc-100', previewColor: 'bg-slate-200' };
}

interface StyleTemplate {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  styleCategory: string;
  previewImageUrl?: string | null;
  recommendedAge?: string | null;
  sortOrder: number;
}

const modelOptions = [
  { id: 'glm-5v-turbo', label: 'GLM-5V', desc: '多模态', icon: '🆓' },
];

const voiceOptions = [
  { id: 'xiaoyan', label: '小燕', desc: '温柔女声' },
  { id: 'xiaoyu', label: '小宇', desc: '活泼男声' },
  { id: 'xiaomei', label: '小美', desc: '可爱童声' },
  { id: 'xiaogang', label: '小刚', desc: '稳重男声' },
];

export default function Step3Visual() {
  const { visual, setVisual, story, setStory } = useCreationStore();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchParams] = useSearchParams();
  const [styleTemplates, setStyleTemplates] = useState<StyleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasLoggedRef = useRef(false);

  // 语速范围（与后端 SPEED_RANGE 对齐）
  const SPEED_MIN = 0.7;
  const SPEED_MAX = 1.3;
  const SPEED_STEP = 0.05;

  // 从 API 加载风格模板
  useEffect(() => {
    const loadStyles = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/style-templates`, {
          headers: { 'Authorization': `Bearer ${token}` },
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('加载风格列表失败');
        const result = await res.json();
        if (result.success && Array.isArray(result.data)) {
          setStyleTemplates(result.data);
        } else {
          setStyleTemplates([]);
        }
      } catch (err) {
        console.error('[Step3Visual] Failed to load styles:', err);
        setError('网络错误，请刷新重试');
      } finally {
        setLoading(false);
      }
    };
    loadStyles();
  }, []);

  // 控制台输出当前页面数据参数
  useEffect(() => {
    if (!hasLoggedRef.current) {
      hasLoggedRef.current = true;
      console.log('[Step3Visual] Component mounted, initial visual data:', {
        style: visual.style,
        model: visual.model,
        voice: visual.voice,
        speed: story.speed,
      });
    }
  }, []);

  useEffect(() => {
    console.log('[Step3Visual] Visual data changed:', {
      style: visual.style,
      model: visual.model,
      voice: visual.voice,
      speed: story.speed,
    });
  }, [visual.style, visual.model, visual.voice, story.speed]);

  // 自动保存到数据库（debounce 2秒）
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    if (!storyId) return;

    const timer = setTimeout(async () => {
      const token = localStorage.getItem('token');
      const store = useCreationStore.getState();
      const data = store.getCreationData();
      const currentStory = store.story;
      const currentVisual = store.visual;

      // 如果数据完全为空，跳过自动保存
      if (!currentVisual.style) {
        console.log('[Step3Visual] Skip auto-save: style empty');
        return;
      }

      try {
        const payload = {
          storyId,
          photoUrl: data.character.photoUrl || '',
          appearance: data.character.appearance || '',
          characterName: data.character.name || '',
          characterType: data.character.characterType || 'human',
          gender: data.character.gender,
          interests: data.character.traits || [],
          targetAge: currentStory.age || '5-6',
          artStyle: currentVisual.style || '',
          theme: currentStory.theme || '',
          pageCount: currentStory.pages || 8,
          outline: currentStory.outline || '',
          speed: currentStory.speed,
          voiceId: currentVisual.voice || 'xiaoyan',
          flow: store.flow,
        };
        console.log('[Step3Visual] Auto-saving draft:', payload);

        const res = await fetch(`${API_BASE_URL}/stories/setup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });
        const result = await res.json();
        if (result.success) {
          console.log('[Step3Visual] Auto-saved to DB, storyId:', result.data?.storyId);
        } else {
          console.error('[Step3Visual] Auto-save failed:', result.error);
        }
      } catch (err) {
        console.error('[Step3Visual] Auto-save request failed:', err);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [visual.style, visual.model, visual.voice, story.speed, searchParams]);

  // 兜底恢复：如果组件 mount 时发现数据为空但 URL 中有 storyId，从数据库恢复
  useEffect(() => {
    const storyId = searchParams.get('storyId');
    const isEmpty = !visual.style;
    if (isEmpty && storyId) {
      console.log('[Step3Visual] Data empty but storyId exists, auto-recovering...');
      recoverFromDB(storyId);
    }
  }, []);

  const recoverFromDB = async (storyId: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/stories/${storyId}/config`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success && result.data) {
        const dbStory = result.data;
        const config = typeof dbStory.config === 'string' ? JSON.parse(dbStory.config) : (dbStory.config || {});
        const audioPref = dbStory.audioPreference as any;
        // 优先从 audioPreference 恢复 voiceId（实际生效的值），其次从 config.voiceId
        setVisual({
          style: dbStory.artStyle || '',
          model: config.model || 'glm-5v-turbo',
          voice: audioPref?.voiceId || config.voiceId || 'xiaoyan',
        });
        // 同步恢复语速（优先 audioPreference，其次 config）
        const recoveredSpeed = audioPref?.speed !== undefined ? audioPref.speed : config.speed;
        if (recoveredSpeed !== undefined) {
          setStory({ speed: recoveredSpeed });
        }
        console.log('[Step3Visual] Auto-recovered from DB');
      }
    } catch (err) {
      console.error('[Step3Visual] Auto-recover failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '40px' }}>
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        <span className="ml-2 text-slate-500">加载风格列表...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center" style={{ padding: '40px' }}>
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-indigo-500 hover:underline"
        >
          刷新重试
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* 插画风格 */}
      <div style={{ padding: '8px 0' }}>
        <label className="block text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
          插画风格 <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {styleTemplates.map((style) => {
            const ui = getCategoryStyle(style.styleCategory);
            return (
              <motion.button
                key={style.key}
                onClick={() => setVisual({ style: style.key })}
                whileHover={{ y: -4, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`
                  relative overflow-hidden rounded-2xl border-2 transition-all duration-300
                  ${visual.style === style.key
                    ? 'border-amber-400 shadow-xl shadow-amber-500/20'
                    : 'border-slate-200 hover:border-indigo-200 hover:shadow-lg'
                  }
                `}
              >
                {/* 预览图区域 */}
                <div className={`aspect-[4/3] bg-gradient-to-br ${ui.gradient} relative`}>
                  {/* 预览缩略图模拟 */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className={`w-20 h-20 ${ui.previewColor} rounded-full opacity-50 blur-xl`} />
                    <span className="absolute text-5xl">{ui.emoji}</span>
                  </div>

                  {/* 选中标记 */}
                  {visual.style === style.key && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-3 right-3 w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </motion.div>
                  )}

                  {/* 底部渐变遮罩（仅保留名称区域） */}
                  <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                  
                  {/* 名称 */}
                  <div className="absolute bottom-0 left-0 right-0 text-white" style={{ padding: '8px' }}>
                    <div className="font-bold text-base">{style.name}</div>
                  </div>
                </div>

                {/* 底部描述区域：白色背景，显示 description + 推荐年龄 */}
                <div className="bg-white text-left" style={{ padding: '8px' }}>
                  <p className="text-xs text-slate-600 line-clamp-2" style={{ marginBottom: '4px' }}>
                    {style.description || ''}
                  </p>
                  {style.recommendedAge && (
                    <span className="text-xs text-slate-400">
                      适合 {style.recommendedAge}
                    </span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* 高级设置（可折叠） */}
      <div className="border border-slate-200 rounded-2xl overflow-hidden" style={{ margin: '8px 0' }}>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-2" style={{ padding: '8px' }}>
            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Wand2 className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="text-left">
              <div className="font-medium text-slate-800">高级设置</div>
              <div className="text-xs text-slate-500">语言模型、音频音色等选项</div>
            </div>
          </div>
          <motion.div
            animate={{ rotate: showAdvanced ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-5 h-5 text-slate-400" />
          </motion.div>
        </button>

        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="border-t border-slate-200 flex flex-col gap-2" style={{ padding: '8px' }}>
                {/* 语言模型 */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    语言模型
                  </label>
                  <div className="flex gap-2">
                    {modelOptions.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => setVisual({ model: model.id as 'glm-5v-turbo' })}
                        className={`
                          flex-1 rounded-xl border-2 transition-all
                          flex items-center gap-2
                          ${visual.model === model.id
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 hover:border-indigo-200'
                          }
                        `}
                        style={{ padding: '8px' }}
                      >
                        <span className="text-xl">{model.icon}</span>
                        <div className="text-left">
                          <div className="font-medium">{model.label}</div>
                          <div className="text-xs opacity-70">{model.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 音频音色 */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
                    <Volume2 className="w-4 h-4 text-indigo-500" />
                    音频音色
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {voiceOptions.map((voice) => (
                      <button
                        key={voice.id}
                        onClick={() => setVisual({ voice: voice.id })}
                        className={`
                          rounded-xl border-2 transition-all text-left
                          ${visual.voice === voice.id
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-slate-200 hover:border-indigo-200'
                          }
                        `}
                        style={{ padding: '8px' }}
                      >
                        <div className="font-medium">{voice.label}</div>
                        <div className="text-xs opacity-70">{voice.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* 语速调节 */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-slate-700" style={{ marginBottom: '8px' }}>
                    <Gauge className="w-4 h-4 text-indigo-500" />
                    语速
                    <span className={`ml-auto text-xs font-medium ${
                      (story.speed || 0.9) < 0.9 ? 'text-blue-600' : (story.speed || 0.9) > 1.1 ? 'text-orange-600' : 'text-green-600'
                    }`}>
                      {(story.speed || 0.9).toFixed(2)}x
                      {(story.speed || 0.9) < 0.9 && ' (慢速)'}
                      {(story.speed || 0.9) > 1.1 && ' (快速)'}
                    </span>
                  </label>
                  <input
                    type="range"
                    min={SPEED_MIN}
                    max={SPEED_MAX}
                    step={SPEED_STEP}
                    value={story.speed || 0.9}
                    onChange={(e) => setStory({ speed: parseFloat(e.target.value) })}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-500"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>慢速 (适合初学)</span>
                    <span>正常</span>
                    <span>快速 (适合进阶)</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
